import {dispatchDaySchema} from './request';

const ZONE = 'Europe/Amsterdam';

/**
 * 08:00 local on the day before, from `docs/timing-and-deadlines.md`.
 *
 * **A cut-off, not the day's anchor.** It defined the dispatch day until 2026-09-21 — the
 * day ran from one deadline to the next — and now only says how late a request may be
 * published for a day that is the calendar day (`docs/answered-questions.md` §30).
 */
const DEADLINE_HOUR = 8;

export const MINUTES_PER_QUARTER = 15;

/** A quarter of an hour in milliseconds — the grid every dispatch timestamp sits on. */
export const QUARTER_MS = MINUTES_PER_QUARTER * 60_000;

const QUARTERS_PER_HOUR = 4;
const HOUR_MS = QUARTERS_PER_HOUR * QUARTER_MS;

const offsetFormatter = new Intl.DateTimeFormat('en-GB', {timeZone: ZONE, timeZoneName: 'longOffset'});

const dayFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Which calendar day it is in Amsterdam at a given instant, as `YYYY-MM-DD`.
 *
 * Assembled from the parts rather than formatted with a locale that happens to emit ISO
 * order, so the shape is ours and not a property of `en-CA`. A sweep runs on a UTC cron
 * and must still reason in the local calendar — the same rule the deadline arithmetic
 * below follows.
 */
export const calendarDayIn = (instant: Date): string => {
  const parts = dayFormatter.formatToParts(instant);
  // Non-null: the formatter was asked for all three, so all three are in its own output.
  const part = (type: Intl.DateTimeFormatPartTypes): string => parts.find(candidate => candidate.type === type)!.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
};

/**
 * The zone's offset at a given instant, in minutes. `Intl` carries the tz database, so
 * this is the real Dutch rule rather than a hand-rolled DST table — including the years
 * when the European Union finally stops changing the clocks.
 */
const offsetMinutesAt = (instant: Date): number => {
  const name = offsetFormatter.formatToParts(instant).find(part => part.type === 'timeZoneName')?.value;
  const match = name ? /GMT([+-])(\d{2}):(\d{2})/.exec(name) : null;
  if (!match) throw new Error(`Could not read the ${ZONE} offset for ${instant.toISOString()}`);
  const [, sign, hours, minutes] = match;
  const magnitude = Number(hours) * 60 + Number(minutes);
  return sign === '-' ? -magnitude : magnitude;
};

/**
 * A whole local hour on a given calendar day, `offsetDays` from the one supplied.
 *
 * Computed by guessing the UTC instant, reading the offset that actually applies there,
 * and correcting once — never by adding hours to a UTC midnight. The rule is anchored in
 * the local calendar, so a fixed offset is wrong for half the year, and the two switch
 * weekends are exactly when a dispatch is most awkward to explain.
 *
 * One correction is enough: the guess is at most an hour out, and 08:00 is nowhere near
 * the 02:00-03:00 window where a corrected instant could land on a different offset.
 */
export const localHour = (dispatchDay: string, offsetDays: number, hour: number): Date =>
  localTime(dispatchDay, offsetDays, hour, 0);

/**
 * The same, to the minute. Quarters need it: the instant of 12:15 local cannot be reached
 * by adding milliseconds to local midnight on a day the clocks move, which is precisely
 * the day it matters.
 */
export const localTime = (dispatchDay: string, offsetDays: number, hour: number, minute: number): Date => {
  const day = dispatchDaySchema.parse(dispatchDay);
  // Destructured with defaults rather than asserted: the schema has already guaranteed
  // three parts, and a `!` that says so is a claim the type system cannot check.
  const [year = 0, month = 1, date = 1] = day.split('-').map(Number);

  const utcAt = (): number => Date.UTC(year, month - 1, date + offsetDays, hour, minute);
  const guess = new Date(utcAt());
  const corrected = new Date(guess.getTime() - offsetMinutesAt(guess) * 60_000);
  return new Date(utcAt() - offsetMinutesAt(corrected) * 60_000);
};

const localEightAm = (dispatchDay: string, offsetDays: number): Date =>
  localHour(dispatchDay, offsetDays, DEADLINE_HOUR);

/** When the window to publish a dispatch day closes: 08:00 in Amsterdam the day before. */
export const publicationDeadline = (dispatchDay: string): Date => localEightAm(dispatchDay, -1);

/**
 * The envelope the day's event covers: constraint 8 of
 * `docs/dispatch-constraints.md`, which is the dispatch day itself.
 *
 * **The day is the Amsterdam calendar day** — `docs/answered-questions.md` §30. So this is
 * midnight to midnight and *not* twenty-four hours: on the two switch days the envelope is
 * 23 or 25 hours long, because that is how long the day is. Computed from two local
 * midnights rather than by adding a span, so the arithmetic cannot claim otherwise.
 */
export const dispatchWindow = (dispatchDay: string): {start: Date; end: Date} => ({
  start: localTime(dispatchDay, 0, 0, 0),
  end: localTime(dispatchDay, 1, 0, 0),
});

/**
 * The event-level `duration` of the day's `FLEX` event, as the DER profile v1.1.0 spells
 * it in `Publication and notice period`: the length of the local calendar day, written in
 * hours.
 *
 * **Never `P1D`, which GOPACS refuses.** Every timestamp on the wire is UTC, and `P1D`
 * reads as one calendar day, so the two disagree on the days the clocks change. Measured
 * from the window rather than from a table, so the envelope and its duration cannot drift
 * apart.
 */
export type DispatchDayDuration = 'PT23H' | 'PT24H' | 'PT25H';

export const dispatchDayDuration = (dispatchDay: string): DispatchDayDuration => {
  const {start, end} = dispatchWindow(dispatchDay);
  const duration = `PT${(end.getTime() - start.getTime()) / HOUR_MS}H`;
  // A Dutch day is 23, 24 or 25 hours. Anything else means the zone's rules changed under
  // us, and a duration the VTN refuses is worse than a sweep that stops and says why.
  if (duration !== 'PT23H' && duration !== 'PT24H' && duration !== 'PT25H') {
    throw new Error(`${dispatchDay} in ${ZONE} is ${duration} long, which no FLEX event can carry`);
  }
  return duration;
};

/**
 * How many quarter-hours the dispatch day holds: 96 on an ordinary day, 92 when the clocks
 * go forward and 100 when they go back.
 *
 * **Not a constant, and that is the point** — `docs/open-questions.md` §31. A quarter number
 * is an index into the day, so a day that is not 24 hours long has not got 96 of them, and
 * a ceiling written as 96 would accept a quarter the day does not contain and resolve it
 * onto the day after.
 */
export const quartersInDispatchDay = (dispatchDay: string): number => {
  const {start, end} = dispatchWindow(dispatchDay);
  return (end.getTime() - start.getTime()) / (MINUTES_PER_QUARTER * 60_000);
};

/**
 * Whether the window a dispatch covered is over. This — not the publication deadline — is
 * what allows silence from a VEN to be read as `NOT_ACKNOWLEDGED`: the deadline passes a
 * full day earlier, while the event is still live.
 */
export const isPastDispatchWindow = (dispatchDay: string, now: Date): boolean =>
  now.getTime() >= dispatchWindow(dispatchDay).end.getTime();

/**
 * A calendar day, shifted. Done in UTC on purpose: a `YYYY-MM-DD` here is a label rather
 * than an instant, and `Date.UTC` is the one arithmetic that cannot be nudged by an
 * offset — a DST switch changes what a day *contains*, never which day follows it.
 */
const addDays = (day: string, offset: number): string => {
  const [year, month, date] = dispatchDaySchema.parse(day).split('-').map(Number);
  return new Date(Date.UTC(year!, month! - 1, date! + offset)).toISOString().slice(0, 10);
};

/**
 * The earliest dispatch day whose window has not closed — the cutoff the request list uses
 * to decide what is still current and what belongs in the archive.
 *
 * A dispatch day is the Amsterdam calendar day (`docs/answered-questions.md` §30), so this
 * is simply that day. It is not `toISOString().slice(0, 10)`: a sweep runs on a UTC cron,
 * and for an hour or two after local midnight the UTC date is still yesterday's.
 *
 * Returned as a day rather than an instant so a query can compare it to a `date` column
 * without teaching Postgres about Amsterdam.
 */
export const currentDispatchDay = (now: Date): string => calendarDayIn(now);

/**
 * The earliest dispatch day somebody can still be approved for. Anything earlier has
 * passed its 08:00 publication deadline and cannot be dispatched, whatever its status.
 *
 * A day before this one that is still `WAITING_FOR_APPROVAL` is the case the list's
 * "needs attention" filter exists for: nothing is technically broken, and the day is
 * already lost.
 *
 * **Derived from the deadline rather than offset from today.** The deadline for a day is
 * 08:00 on the day *before* it, so at 09:00 on the 12th the 13th is already gone and the
 * 14th is the first that is not — two days out, not one. Written as a walk from the
 * current day so the arithmetic cannot drift from `isPastDeadline`, which is what the API
 * actually refuses with; it takes at most two steps.
 */
export const firstApprovableDispatchDay = (now: Date): string => {
  let candidate = currentDispatchDay(now);
  while (isPastDeadline(candidate, now)) candidate = addDays(candidate, 1);
  return candidate;
};

/**
 * The last dispatch day whose publication deadline has already passed, and the last whose
 * steering day is over. Everything up to and including each is beyond saving, which is what
 * lets a sweep ask the database for its work instead of reading rows it will skip.
 *
 * Walked from the current day rather than offset from it, for the reason
 * `firstApprovableDispatchDay` gives: the deadline is 08:00 on the day *before*, so how far
 * out it reaches depends on the hour. Both take at most two steps.
 */
export const expiredThrough = (now: Date): {byDeadline: string; byDay: string} => {
  let byDeadline = currentDispatchDay(now);
  while (isPastDeadline(addDays(byDeadline, 1), now)) byDeadline = addDays(byDeadline, 1);

  let byDay = currentDispatchDay(now);
  while (!isPastDispatchWindow(byDay, now)) byDay = addDays(byDay, -1);

  return {byDeadline: isPastDeadline(byDeadline, now) ? byDeadline : addDays(byDeadline, -1), byDay};
};

/** `now >= deadline`: at 08:00 exactly the window has closed, not a millisecond later. */
export const isPastDeadline = (dispatchDay: string, now: Date): boolean =>
  now.getTime() >= publicationDeadline(dispatchDay).getTime();

/**
 * The instant a quarter-hour of Solvie's profile starts.
 *
 * **Quarter `0` is local midnight of the dispatch day**, and the day never leaves its own
 * date: a dispatch day is the Amsterdam calendar day (`docs/answered-questions.md` §30).
 * The split this function used to make between two dates went with the 08:00 boundary, and
 * with it the one piece of arithmetic that could put a dispatch on the wrong date.
 *
 * **The ceiling is the day's own** — 92, 96 or 100 — so a quarter the day does not contain
 * is refused here rather than resolving onto the day after.
 *
 * Anchored to the local clock rather than added as milliseconds to midnight, because on the
 * two switch days those are different instants, and the difference is a whole hour of
 * dispatch.
 */
export const quarterStart = (dispatchDay: string, quarterNr: number): Date => {
  const {start} = dispatchWindow(dispatchDay);
  const quarters = quartersInDispatchDay(dispatchDay);
  if (!Number.isInteger(quarterNr) || quarterNr < 0 || quarterNr >= quarters) {
    throw new RangeError(`A quarter number is 0 to ${quarters - 1} on ${dispatchDay}, not ${quarterNr}`);
  }
  // Elapsed quarters from the day's start, not `hour * 4` on the local clock. On the two
  // switch days those disagree by an hour: 02:00 happens twice in October and not at all in
  // March, so the nth quarter of the day is not the one the clock face suggests. Counting
  // elapsed time is also what a solver producing 92 or 100 quarters is counting.
  return new Date(start.getTime() + quarterNr * MINUTES_PER_QUARTER * 60_000);
};

/**
 * The instant a congestion window's bound falls on, to the quarter.
 *
 * **`bound` is not decoration: `00:00` names two instants.** A day opens at midnight and
 * closes at the next one, and `windowEndTimesFor` offers the closing one precisely so the
 * end of the day can be asked for. Reading both ends by the same rule turns `22:00`–`00:00`
 * into a window that ends before it starts.
 */
export const windowInstant = (dispatchDay: string, time: string, bound: 'from' | 'to' = 'from'): Date => {
  const hour = Number(time.slice(0, 2));
  const minute = Number(time.slice(3, 5));
  return localTime(dispatchDay, bound === 'to' && hour === 0 && minute === 0 ? 1 : 0, hour, minute);
};

/**
 * How long a window actually lasts on this dispatch day, in hours.
 *
 * **Not the span on the clock.** The contract judges `from` and `to` as clock faces,
 * because it validates a window without knowing which day it is for — and on the two days a
 * year the clocks move, the two disagree. `01:00`–`03:00` takes one hour on 28 March 2027
 * and three on 25 October 2026, and `windowTimeExists` finds nothing wrong with either: on
 * the spring day both hours exist, the missing one is between them, and on the autumn day
 * one of them simply happens twice.
 *
 * Measured between instants for that reason, rather than by subtracting clock readings.
 */
export const windowElapsedHours = (dispatchDay: string, from: string, to: string): number =>
  (windowInstant(dispatchDay, to, 'to').getTime() - windowInstant(dispatchDay, from).getTime()) / HOUR_MS;

const clockFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/**
 * Whether the time a window names happens at all on this dispatch day.
 *
 * **The day the clocks go forward has no 02:00 to 02:45**, and asking for an instant that
 * does not exist does not fail — `localTime` lands an hour later, quietly. So `01:00`–`02:15`
 * becomes a window ending at 03:15, and the caller is never told that the window solved is
 * not the window they picked.
 *
 * Answered by round trip rather than by a DST table: resolve the time, read the clock back
 * in Amsterdam, and see whether it says what was asked for.
 */
export const windowTimeExists = (dispatchDay: string, time: string, bound: 'from' | 'to' = 'from'): boolean => {
  const at = windowInstant(dispatchDay, time, bound);
  const reached = clockFormatter.format(at);
  const day = calendarDayIn(at);
  const expectedDay =
    bound === 'to' && time === '00:00' ? addDays(dispatchDaySchema.parse(dispatchDay), 1) : dispatchDay;
  return reached === time && day === expectedDay;
};
